import React, { ReactElement, useEffect, useState } from 'react';
import { PathMatch } from '@remix-run/router/utils';
import {
    createRoutesFromElements,
    matchPath,
    Navigate,
    Outlet,
    Route,
    Routes,
    useLocation,
} from 'react-router-dom';
import { OfferAgreement } from '../data-layer/business/types';
import { PostStatus } from '../data-layer/posts/constants';
import { DashboardLayout as DashboardLayoutUI } from '../ui/dashboard/DashboardLayout';
import { IconAdd } from '../ui/icon/IconAdd';
import { IconFeed } from '../ui/icon/IconFeed';
import { IconHome } from '../ui/icon/IconHome';
import { IconHomeFilled } from '../ui/icon/IconHomeFilled';
import { IconSearch } from '../ui/icon/IconSearch';
import { Avatar } from '../ui/media/Avatar';
import { LocationContext, LocationContextPayload } from '../utils/LocationContext';
import { isDeclinedAdmin, isSuperAdmin } from '../utils/accessService';
import { getWindow } from '../utils/getWindow';
import { useLocationListener } from '../utils/history';
import { useMeta } from '../utils/useMeta';
import { useScrollRestoration } from '../utils/useScrollRestoration';
import { AppDiscovery } from './AppDiscovery';
import { DialogRoute, useOpenDialog } from './ModalRoute';
import { BusinessDetails } from './business/BusinessDetails';
import { BusinessManagement } from './business/BusinessManagement';
import { BusinessPersistence } from './business/BusinessPersistence';
import { BusinessRegistration } from './business/BusinessRegistration';
import { BusinessStatisticSummary } from './business/BusinessStatisticSummary';
import { FeedManagement } from './business/FeedManagement';
import { InviteMemberDialog } from './business/InviteMemberDialog';
import { OfferAgreementDialog } from './business/OfferAgreementDialog';
import { SelectCategoriesDialog } from './business/SelectCategoriesDialog';
import { WithoutRangeDialog } from './business/WithoutRangeDialog';
import { makeBusinessPostsTab } from './business/makeBusinessPostsTab';
import { DIALOGS } from './constants';
import { ErrorPage } from './error/Error';
import { Login } from './login/Login';
import { PostDetails } from './post/PostDetails';
import { PostFeed } from './post/PostFeed';
import { PostPersistence } from './post/PostPersistence';
import { PostProductsDialog } from './post/PostProductsDialog';
import { ReportDialog } from './post/ReportDialog';
import { Search } from './search/Search';
import { CollectionDetails } from './users/CollectionDetails';
import { DeleteFromCollectionDialog } from './users/DeleteFromCollectionDialog';
import { EditCollectionDialog } from './users/EditCollectionDialog';
import { SaveToCollectionDialog } from './users/SaveToCollectionDialog';
import { UserDetails } from './users/UserDetails';
import { UserSettings } from './users/UserSettings';
import { useCurrentUser } from './users/useCurrentUser';
import { withBusinessAuthProtection } from './withBusinessAuthProtection';
import type { UserModel } from '../data-layer/user/types';

const buildMenuItems = (currentUser: UserModel | undefined) => [
    {
        selectedIcon: <IconHomeFilled />,
        unselectedIcon: <IconHome />,
        title: 'Лента',
        to: '/',
        end: true,
    },
    {
        selectedIcon: <IconSearch stroke="currentcolor" />,
        unselectedIcon: <IconSearch stroke="none" />,
        title: 'Поиск',
        to: `/search?searchType=${isSuperAdmin(currentUser?.role) ? 'post' : 'business'}&query=${
            getWindow()?.sessionStorage.getItem('lastSearch') ?? ''
        }`,
        end: true,
    },
    {
        selectedIcon: (
            <Avatar
                src={currentUser?.avatarUrl}
                title={currentUser?.displayName}
                size={24}
                border="2px solid var(--color-primary)"
            />
        ),
        unselectedIcon: (
            <Avatar src={currentUser?.avatarUrl} title={currentUser?.displayName} size={24} />
        ),
        title: 'Профиль',
        to: '/profile',
    },

    ...(currentUser?.profile
        ? [
              {
                  selectedIcon: (
                      <Avatar
                          src={currentUser.profile.avatarUrl}
                          title={currentUser.profile.displayName}
                          size={24}
                          border="2px solid var(--color-primary)"
                      />
                  ),
                  unselectedIcon: (
                      <Avatar
                          src={currentUser.profile.avatarUrl}
                          title={currentUser.profile.displayName}
                          size={24}
                      />
                  ),
                  title: currentUser.profile.displayName,
                  subtitle: 'бизнес-профиль',
                  to: `/businesses/${currentUser.profile.publicId}`,
                  end: true,
              },
          ]
        : []),
    ...(currentUser?.profile && !isDeclinedAdmin(currentUser?.role)
        ? [
              {
                  selectedIcon: <IconFeed />,
                  unselectedIcon: <IconFeed />,
                  title: 'Управление лентой',
                  to: `/businesses/${currentUser.profile.publicId}/management`,
                  end: true,
              },
              {
                  selectedIcon: <IconAdd />,
                  unselectedIcon: <IconAdd />,
                  title: 'Добавить пост',
                  to: '/posts/create',
                  end: true,
              },
          ]
        : []),
];

// Порядок имеет значение
const BLACKLIST: Array<[string, (matched: PathMatch | null) => string | undefined]> = [
    ['/businesses/:businessId/management', (matched) => matched?.pathnameBase ?? undefined],
    ['/businesses/:businessId/*', (matched) => matched?.pathnameBase ?? undefined],
    ['/', (matched) => matched?.pathnameBase ?? undefined],
];

const HistoryListener = (props: { children: ReactElement }) => {
    useMeta({});
    useLocationListener();
    useScrollRestoration({
        buildLocationKey: (location) => {
            const key = BLACKLIST.reduce<string | undefined>(
                (builtKey, [route, buildLocationKey]) => {
                    const matched = matchPath(route, location.pathname);

                    return builtKey || buildLocationKey(matched);
                },
                undefined,
            );

            return key ?? location.pathname;
        },
    });

    const location = useLocation();
    const [locationInfo, setLocationInfo] = useState<LocationContextPayload>({});

    useEffect(() => {
        if (location.pathname !== locationInfo.currentLocation) {
            setLocationInfo({
                currentLocation: location.pathname,
                previousLocation: locationInfo.currentLocation,
            });
        }
    }, [location, locationInfo.currentLocation, setLocationInfo]);

    return (
        <LocationContext.Provider value={locationInfo}>{props.children}</LocationContext.Provider>
    );
};

const BusinessPostsOverviewTab = makeBusinessPostsTab('business-overview-', {
    statuses: [PostStatus.PUBLISHED, PostStatus.LIMITED],
});

const BusinessPostsModerationTab = withBusinessAuthProtection(
    makeBusinessPostsTab('business-moderation-', {
        statuses: [
            PostStatus.MODERATION,
            PostStatus.DECLINED,
            PostStatus.PUBLISHED,
            PostStatus.LIMITED,
        ],
    }),
);

const NotFound = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return <Navigate to="/" />;
};

const DashboardLayout = () => {
    const currentUser = useCurrentUser();
    const openDialog = useOpenDialog();

    useEffect(() => {
        if (
            currentUser?.profile?.offerAgreement &&
            currentUser.profile.offerAgreement === OfferAgreement.UNSET &&
            currentUser.profile.id
        ) {
            openDialog(DIALOGS.OFFER_AGREEMENT, { businessId: currentUser?.profile?.id });
        }
    }, [currentUser?.profile?.offerAgreement, currentUser?.profile?.id]);

    return (
        <DashboardLayoutUI menuItems={buildMenuItems(currentUser)}>
            <Outlet />
            <DialogRoute name={DIALOGS.EDIT_COLLECTION} element={<EditCollectionDialog />} />
            <DialogRoute
                name={DIALOGS.SAVE_TO_COLLECTION_DIALOG_NAME}
                element={<SaveToCollectionDialog />}
            />
            <DialogRoute
                name={DIALOGS.DELETE_FROM_COLLECTION_DIALOG_NAME}
                element={<DeleteFromCollectionDialog />}
            />
            <DialogRoute name={DIALOGS.CREATE_INVITE} element={<InviteMemberDialog />} />
            <DialogRoute name={DIALOGS.REPORT_DIALOG} element={<ReportDialog />} />
            <DialogRoute name={DIALOGS.OFFER_AGREEMENT} element={<OfferAgreementDialog />} />
            <DialogRoute name={DIALOGS.WITHOUT_RANGE} element={<WithoutRangeDialog />} />
            <DialogRoute name={DIALOGS.SELECT_CATEGORIES} element={<SelectCategoriesDialog />} />
            <DialogRoute name={DIALOGS.POST_PRODUCTS} element={<PostProductsDialog />} />
        </DashboardLayoutUI>
    );
};

export const getRootRoute = () => (
    <Route
        path="*"
        element={
            <HistoryListener>
                <>
                    <Routes>
                        <Route path="not-found" element="" />
                        <Route path="login" element={<Login />} />
                        <Route path="users/current/settings" element={<UserSettings />} />
                        <Route path="/" element={<DashboardLayout />}>
                            <Route index element={<PostFeed />} />
                            <Route path="posts/create" element={<PostPersistence />} />
                            <Route path="posts/:postId/edit" element={<PostPersistence />} />
                            <Route
                                path="business/:businessId/posts/:postId"
                                element={<PostDetails />}
                            />
                            <Route path="business/posts/:postId" element={<PostDetails />} />
                            <Route
                                path="businesses/:businessId/management"
                                element={<BusinessManagement />}
                            >
                                <Route index element={<FeedManagement />} />
                                <Route path="statistic" element={<BusinessStatisticSummary />} />
                            </Route>
                            <Route path="businesses/:businessId/*" element={<BusinessDetails />}>
                                <Route index element={<BusinessPostsOverviewTab />} />
                                <Route path="moderation" element={<BusinessPostsModerationTab />} />
                            </Route>
                            <Route
                                path="businesses/edit/:businessId"
                                element={<BusinessPersistence />}
                            />
                            <Route
                                path="businesses/registration"
                                element={<BusinessRegistration />}
                            />
                            <Route path="search" element={<Search />} />
                            <Route path="profile" element={<UserDetails />} />
                            <Route
                                path="profile/collections/:collectionId"
                                element={<CollectionDetails />}
                            />
                            <Route path="error/451" element={<ErrorPage />} />
                        </Route>
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                    <AppDiscovery />
                </>
            </HistoryListener>
        }
    />
);

export const routes = createRoutesFromElements(getRootRoute());
